import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { getApiUrl } from '../utils/nativeApi';
import { getSession, isNativePlatform } from '../utils/nativeAuth';

function ImportModal({ isOpen, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const API_URL = getApiUrl();
      const headers = {};
      if (isNativePlatform()) {
        const token = await getSession();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(`${API_URL}/api/expenses/import`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        if (data.imported > 0) {
          toast.success(`Imported ${data.imported} expenses!`);
        }
      } else {
        toast.error(data.error || 'Failed to import expenses');
      }
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Failed to import expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const API_URL = getApiUrl();
      const headers = {};
      if (isNativePlatform()) {
        const token = await getSession();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(`${API_URL}/api/expenses/import/template`, {
        credentials: 'include',
        headers,
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'expenses_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Template downloaded!');
      } else {
        toast.error('Failed to download template');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black">📥 Import Expenses</h3>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Instructions */}
        <div className="mb-4 p-3 bg-blue-50 border-2 border-black rounded-lg">
          <h4 className="font-bold mb-2">📋 CSV Format Instructions</h4>
          <p className="text-sm text-gray-700 mb-2">
            Upload a CSV file <strong>without a header line</strong> with the following columns in order:
          </p>
          <ol className="text-sm text-gray-700 list-decimal list-inside space-y-1">
            <li><strong>title</strong> - Short description (e.g., Lunch, Groceries) or empty</li>
            <li><strong>amount</strong> - Number (e.g., 50.00)</li>
            <li><strong>currency</strong> - 3-letter code (e.g., USD, ILS, EUR)</li>
            <li><strong>category</strong> - Category name without emoji (e.g., Food & Dining, Groceries) or empty</li>
            <li><strong>note</strong> - Text or empty</li>
            <li><strong>created_at</strong> - Date in DD-MM-YYYY format</li>
            <li><strong>household_code</strong> - 6-digit household invite code or empty</li>
          </ol>
          <p className="text-xs text-gray-500 mt-2">
            ⚠️ Category names must match exactly (no emoji). Invalid currencies will be skipped.
          </p>
        </div>

        {/* Download Template */}
        <div className="mb-4">
          <button
            onClick={handleDownloadTemplate}
            className="w-full px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100 transition-colors"
          >
            📄 Download Template CSV
          </button>
        </div>

        {/* File Upload */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">Select CSV File</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm file:mr-3 file:px-3 file:py-1 file:border-0 file:bg-primary file:font-bold file:rounded"
          />
          {file && (
            <p className="text-sm text-gray-600 mt-1">
              Selected: {file.name}
            </p>
          )}
        </div>

        {/* Import Result */}
        {result && (
          <div className={`mb-4 p-3 border-2 border-black rounded-lg ${
            result.skipped > 0 ? 'bg-yellow-50' : 'bg-green-50'
          }`}>
            <h4 className="font-bold mb-2">📊 Import Summary</h4>
            <div className="space-y-1 text-sm">
              <p className="text-green-700">
                ✅ Successfully imported: <strong>{result.imported}</strong>
              </p>
              {result.skipped > 0 && (
                <p className="text-yellow-700">
                  ⚠️ Skipped: <strong>{result.skipped}</strong>
                </p>
              )}
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-bold text-red-600">Errors:</p>
                <ul className="text-xs text-red-600 list-disc list-inside">
                  {result.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleImport}
              disabled={loading || !file}
              className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 disabled:opacity-50"
            >
              {loading ? 'Importing...' : '📤 Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportModal;
